import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const SUBCATS = {
  "mobile-accessories": [
    { v: "phone-cases", l: "Phone Cases" },
    { v: "chargers", l: "Chargers" },
    { v: "usb-cables", l: "USB Cables" },
    { v: "power-banks", l: "Power Banks" },
    { v: "earbuds", l: "Earbuds" },
    { v: "screen-protectors", l: "Screen Protectors" },
    { v: "phone-holders", l: "Phone Holders" },
  ],
  lifestyle: [
    { v: "rings", l: "Premium Rings" },
    { v: "bracelets", l: "Bracelets" },
    { v: "chains", l: "Chains" },
    { v: "wallets", l: "Wallets" },
  ],
};

export default function ShopPage({ initialCategory }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [sort, setSort] = useState("newest");

  const category = initialCategory || searchParams.get("category") || "";
  const isBestSeller = searchParams.get("best_seller") === "1";
  const isNew = searchParams.get("new") === "1";

  const availableSubs = useMemo(() => {
    if (category === "mobile-accessories") return SUBCATS["mobile-accessories"];
    if (category === "lifestyle") return SUBCATS.lifestyle;
    return [...SUBCATS["mobile-accessories"], ...SUBCATS.lifestyle];
  }, [category]);

  useEffect(() => {
    setLoading(true);
    const params = { sort, limit: 100 };
    if (category) params.category = category;
    if (isBestSeller) params.best_seller = true;
    if (isNew) params.new_arrival = true;
    if (priceRange[0] > 0) params.min_price = priceRange[0];
    if (priceRange[1] < 10000) params.max_price = priceRange[1];
    api.get("/products", { params }).then((r) => {
      let data = r.data;
      if (selectedSubs.length > 0) data = data.filter((p) => selectedSubs.includes(p.subcategory));
      setProducts(data);
    }).finally(() => setLoading(false));
  }, [category, isBestSeller, isNew, priceRange, selectedSubs, sort]);

  const title = initialCategory === "mobile-accessories" ? "Mobile Accessories"
    : initialCategory === "lifestyle" ? "Lifestyle"
    : isBestSeller ? "Best Sellers"
    : isNew ? "New Arrivals"
    : "Shop All";

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Shop</div>
        <h1 className="mt-2 font-heading font-medium text-4xl sm:text-6xl tracking-tight">{title}</h1>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-10">
        {/* Filters */}
        <aside className="space-y-8 lg:sticky lg:top-24 lg:self-start">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Price</div>
            <Slider min={0} max={10000} step={100} value={priceRange} onValueChange={setPriceRange} data-testid="filter-price" />
            <div className="flex justify-between mt-2 text-xs text-muted-foreground tabular-nums">
              <span>₹{priceRange[0]}</span><span>₹{priceRange[1]}</span>
            </div>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Type</div>
            <div className="space-y-2">
              {availableSubs.map((s) => (
                <label key={s.v} className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    data-testid={`filter-sub-${s.v}`}
                    checked={selectedSubs.includes(s.v)}
                    onCheckedChange={(c) => {
                      setSelectedSubs((prev) => c ? [...prev, s.v] : prev.filter((x) => x !== s.v));
                    }}
                  />
                  <span>{s.l}</span>
                </label>
              ))}
            </div>
          </div>
        </aside>

        {/* Products */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <div className="text-sm text-muted-foreground">{products.length} products</div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-44" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-square rounded-2xl bg-secondary/60 animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-12 text-center">
              <div className="font-heading text-xl mb-2">No products yet</div>
              <div className="text-sm text-muted-foreground">Products will appear here as they're added to the store.</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
